/**
 * HTTP Client - 统一的 HTTP 请求客户端
 * 
 * 重要说明:
 * 1. 本模块使用全局 axios 实例,会自动继承 axios.config.js 中配置的所有拦截器
 * 2. axios.config.js 已在 index.jsx 中全局引入,提供统一的:
 *    - 响应拦截和错误处理
 *    - JsonBig 大数字处理
 *    - 参数序列化配置
 *    - 登录态检查
 * 3. 本模块仅添加额外的请求增强功能:
 *    - Token 注入
 *    - Trace ID 追踪
 *    - 请求日志
 */

import axios, {
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";

// 确保 axios.config.js 已加载 (在 index.jsx 中已全局引入)
import "../axios.config";

// Utility function to generate trace ID
function generateTraceId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// 添加请求拦截器 - 注入 Token 和 Trace ID
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add authentication token if available
    const token = localStorage.getItem("Cookie");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add trace ID for request tracking
    const traceId = generateTraceId();
    if (config.headers) {
      config.headers["X-Trace-ID"] = traceId;
    }

    // Log request for debugging
    console.log(
      `[HTTP Request] ${config.method?.toUpperCase()} ${config.url}`,
      {
        traceId,
        data: config.data,
        params: config.params,
      }
    );

    return config;
  },
  (error: AxiosError) => {
    console.error("[HTTP Request Error]", error);
    return Promise.reject(error);
  }
);

// 使用全局 axios 实例
// 注意: 不使用 axios.create(),以确保继承 axios.config.js 的所有配置
const httpClient = axios;

// Utility function to handle API errors
export const handleAPIError = (error: any) => {
  if (error.apiError) {
    console.error(`API Error [${error.traceId}]:`, error.message);
    return {
      success: false,
      errorMessage: error.message,
      errorCode: error.errorCode,
      traceId: error.traceId,
    };
  } else if (error.networkError) {
    console.error("Network Error:", error.message);
    return {
      success: false,
      errorMessage: "网络连接异常,请检查网络后重试",
      errorCode: "NETWORK_ERROR",
    };
  } else {
    console.error("Unknown Error:", error.message);
    return {
      success: false,
      errorMessage: "未知错误,请稍后重试",
      errorCode: "UNKNOWN_ERROR",
    };
  }
};

export default httpClient;
